import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  DatePicker,
  Button,
  Select,
  Row,
  Col,
  Spin,
  message,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import api from '../services/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const Reportes = () => {
  const [loading, setLoading] = useState(false);
  const [tipoReporte, setTipoReporte] = useState('servicios');
  const [fechaInicio, setFechaInicio] = useState(dayjs().startOf('month'));
  const [fechaFin, setFechaFin] = useState(dayjs().endOf('month'));
  const [datosServicios, setDatosServicios] = useState([]);
  const [datosIngresos, setDatosIngresos] = useState([]);
  const [datosTecnicos, setDatosTecnicos] = useState([]);

  useEffect(() => {
    cargarReporte();
  }, []);

  const cargarReporte = async () => {
    setLoading(true);
    try {
      const fechaInicioStr = fechaInicio.format('YYYY-MM-DD');
      const fechaFinStr = fechaFin.format('YYYY-MM-DD');

      switch (tipoReporte) {
        case 'servicios':
          const responseServicios = await api.get(
            `/reportes?tipo=servicios&fecha_inicio=${fechaInicioStr}&fecha_fin=${fechaFinStr}`
          );
          setDatosServicios(responseServicios.data);
          break;
        case 'ingresos':
          const responseIngresos = await api.get(
            `/reportes?tipo=ingresos&fecha_inicio=${fechaInicioStr}&fecha_fin=${fechaFinStr}`
          );
          setDatosIngresos(responseIngresos.data);
          break;
        case 'personal':
          const responsePersonal = await api.get(
            `/reportes?tipo=personal&fecha_inicio=${fechaInicioStr}&fecha_fin=${fechaFinStr}`
          );
          setDatosTecnicos(responsePersonal.data);
          break;
      }
    } catch (error) {
      message.error('Error al cargar reporte');
    } finally {
      setLoading(false);
    }
  };

  const columnsServicios = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Cliente',
      key: 'cliente',
      render: (_, record) =>
        `${record.cliente_nombre} ${record.cliente_apellido}`,
    },
    {
      title: 'Vehículo',
      key: 'vehiculo',
      render: (_, record) => {
        const marca = record.marca || '';
        const modelo = record.modelo || '';
        const placa = record.placa || '';
        const vehiculoStr = [marca, modelo, placa].filter(Boolean).join(' ');
        return vehiculoStr || '-';
      },
    },
    {
      title: 'Tipo de Servicio',
      key: 'tipo_servicio',
      render: (_, record) => {
        if (record.tipo_servicio_codigo && record.tipo_servicio) {
          return `${record.tipo_servicio_codigo} - ${record.tipo_servicio}`;
        }
        return record.tipo_servicio || '-';
      },
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
    },
    {
      title: 'Costo',
      dataIndex: 'costo',
      key: 'costo',
      render: (costo) => `S/ ${costo}`,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha_solicitud',
      key: 'fecha',
    },
  ];

  const columnsTecnicos = [
    {
      title: 'Personal',
      key: 'personal',
      render: (_, record) => record.nombre_completo || '-',
    },
    {
      title: 'Cargo',
      dataIndex: 'cargo',
      key: 'cargo',
    },
    {
      title: 'Total Servicios',
      dataIndex: 'total_servicios',
      key: 'total_servicios',
    },
    {
      title: 'Total Ingresos',
      dataIndex: 'total_ingresos',
      key: 'total_ingresos',
      render: (ingresos) => `S/ ${ingresos || 0}`,
    },
  ];

  return (
    <div>
      <Card
        title="Reportes"
        extra={
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={cargarReporte}
            loading={loading}
          >
            Generar Reporte
          </Button>
        }
      >
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <Select
              value={tipoReporte}
              onChange={setTipoReporte}
              style={{ width: '100%' }}
            >
              <Select.Option value="servicios">Servicios por Fecha</Select.Option>
              <Select.Option value="ingresos">Ingresos por Fecha</Select.Option>
              <Select.Option value="personal">Servicios por Personal</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={16}>
            <RangePicker
              value={[fechaInicio, fechaFin]}
              onChange={(dates) => {
                if (dates) {
                  setFechaInicio(dates[0]);
                  setFechaFin(dates[1]);
                }
              }}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 50 }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            {tipoReporte === 'servicios' && (
              <>
                <Table
                  columns={columnsServicios}
                  dataSource={datosServicios}
                  rowKey="id"
                  style={{ marginBottom: 24 }}
                />
              </>
            )}

            {tipoReporte === 'ingresos' && (
              <>
                <Card title="Gráfico de Ingresos" style={{ marginBottom: 24 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={datosIngresos}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="fecha" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="total"
                        stroke="#8884d8"
                        name="Ingresos (S/)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              </>
            )}

            {tipoReporte === 'personal' && (
              <>
                <Card title="Gráfico de Servicios por Personal" style={{ marginBottom: 24 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={datosTecnicos}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="nombre_completo" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="total_servicios" fill="#8884d8" name="Total Servicios" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
                <Table
                  columns={columnsTecnicos}
                  dataSource={datosTecnicos}
                  rowKey="nombre_completo"
                />
              </>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default Reportes;

